import React from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import { PARENT_MERGE_FIELDS } from "../utils/mergeParentFields";

const ParentComparisonStep = ({
  existingParent1,
  newParent1,
  existingParent2,
  newParent2,
  parent1Choices,
  setParent1Choices,
  parent2Choices,
  setParent2Choices,
  mode = "application-conflict",
}) => {
  const { t } = useTranslation();

  const fields = PARENT_MERGE_FIELDS;
  const existingLabel =
    mode === "manual-parent-merge"
      ? t("parentMerge.parentA", { defaultValue: "Parent A" })
      : t("applications.existing_data");
  const incomingLabel =
    mode === "manual-parent-merge"
      ? t("parentMerge.parentB", { defaultValue: "Parent B" })
      : t("applications.new_data");

  const setAllChoices = (setFn, type) => {
    const newChoices = {};
    fields.forEach((field) => {
      newChoices[field] = type;
    });
    setFn(newChoices);
  };

  const renderValue = (val, selected, type, onClick) => {
    const isSelected = selected === type;
    const baseClass =
      "p-2 rounded-lg cursor-pointer flex items-center justify-between w-full border transition";
    const selectedClass =
      type === "existing"
        ? "bg-blue-100 border-blue-400 dark:bg-blue-900/40 dark:border-blue-400/60"
        : "bg-green-100 border-green-400 dark:bg-green-900/40 dark:border-green-400/60";
    const hoverClass = "hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600";

    return (
      <div className={`${baseClass} ${isSelected ? selectedClass : hoverClass}`} onClick={onClick}>
        <span className="font-mono text-sm truncate text-gray-800 dark:text-gray-100">{val || "-"}</span>
        {isSelected && (
          <FaCheckCircle
            className={`ml-2 ${type === "existing" ? "text-blue-500 dark:text-blue-300" : "text-green-600 dark:text-green-300"}`}
          />
        )}
      </div>
    );
  };

  const renderParentSection = (title, existing, incoming, choices, setChoices) => (
    <div className="mb-6">
      <div className="mb-8 rounded-xl border border-gray-200 bg-white/90 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/80">
        <div className="grid grid-cols-3 gap-4">
          {/* Existing Values */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col items-start mb-1">
              <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                {existingLabel}
              </span>
              <button
                onClick={() => setAllChoices(setChoices, "existing")}
                className="mt-1 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-800/40"
              >
                {t("applications.use_all_existing")}
              </button>
            </div>
            {fields.map((field) =>
              renderValue(existing[field], choices[field] || "existing", "existing", () =>
                setChoices((prev) => ({ ...prev, [field]: "existing" }))
              )
            )}
          </div>

          {/* Field Labels */}
          <div className="bg-gray-100 rounded-lg py-2 px-3 flex flex-col justify-center gap-2 dark:bg-gray-800/70">
            <span className="text-center font-semibold text-sm text-gray-700 dark:text-gray-200 mb-1">{title}</span>
            {fields.map((field) => (
              <div key={field} className="text-center text-sm text-gray-700 dark:text-gray-300">
                {t(`parent.${field}`)}
              </div>
            ))}
          </div>

  {/* New Values */}
          <div className="flex flex-col gap-2 items-start">
            <div className="flex flex-col items-start mb-1">
              <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                {incomingLabel}
              </span>
              <button
                onClick={() => setAllChoices(setChoices, "new")}
                className="mt-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded hover:bg-green-200 dark:bg-green-900/30 dark:text-green-200 dark:hover:bg-green-800/40"
              >
                {t("applications.use_all_new")}
              </button>
            </div>
            {fields.map((field) =>
              renderValue(incoming[field], choices[field] || "existing", "new", () =>
                setChoices((prev) => ({ ...prev, [field]: "new" }))
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUnmatchedParent = (title, incoming) => (
    <div className="mb-6 rounded-xl border border-green-200 bg-green-50/80 p-4 shadow-sm dark:border-green-700/50 dark:bg-green-950/30">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="mt-1 text-xs text-green-800 dark:text-green-200">
        {t("applications.parent_no_existing_match", {
          defaultValue: "No existing match was found. This incoming parent will be added without merging.",
        })}
      </p>
      <dl className="mt-3 grid gap-2 sm:grid-cols-2">
        {fields.map((field) => (
          <div key={field} className="min-w-0">
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{t(`parent.${field}`)}</dt>
            <dd className="truncate text-sm text-gray-900 dark:text-gray-100">{incoming?.[field] || "-"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <>
      {newParent1 && (existingParent1
        ? renderParentSection(t("applications.parent_1"), existingParent1, newParent1, parent1Choices, setParent1Choices)
        : renderUnmatchedParent(t("applications.parent_1"), newParent1))}

      {newParent2 && (existingParent2
        ? renderParentSection(t("applications.parent_2"), existingParent2, newParent2, parent2Choices, setParent2Choices)
        : renderUnmatchedParent(t("applications.parent_2"), newParent2))}
    </>
  );
};

export default ParentComparisonStep;
